import {
  Item,
  Variant as ProductVariant,
} from "@aitisal/contracts/restaurant/menu";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { getTitle } from "../helper/helper";
import { useCart } from "../stores/useCart";
import { useWishlistStore } from "../stores/wishlistStore";

/**
 * Shared purchase logic for the product details page (money path).
 *
 * Owns variant matching, customization selection/validation, price and
 * discount computation, cart payload formatting and buy-now navigation.
 * Theme page slots are presentation-only over this hook — never re-implement
 * money logic inside a theme folder.
 */

export interface CustomizationOption {
  customizationOptionId: string;
  itemId: string;
  name: string;
  price: number;
  isSelected: boolean;
  minQuantity: number;
  maxQuantity: number;
  quantity: number;
}

export interface ProductCustomization {
  customizationId: string;
  name: string;
  displayType: string;
  minSelectable: number;
  maxSelectable: number;
  isOptional: boolean;
  options: CustomizationOption[];
}

export interface ProductData {
  rawItem: Item;
  title: string;
  price: string | number;
  description: string;
  customizations: ProductCustomization[];
}

export const findMatchingVariant = (
  variants: ProductVariant[] | undefined,
  selectedOptionByAxis: Record<string, string>,
): ProductVariant | undefined => {
  if (!variants || variants.length === 0) return undefined;
  return variants.find((v) =>
    v.attributeValues.every(
      (av) =>
        av.attributeId &&
        selectedOptionByAxis[av.attributeId] === av.attributeOptionId,
    ),
  );
};

export function mapProductData(item: Item): ProductData {
  const mapCustomizations = (): ProductCustomization[] => {
    if (!item?.customizations) return [];

    return item.customizations.map((itemCustomization) => ({
      customizationId: itemCustomization.customizationId,
      name: getTitle(itemCustomization.name),
      displayType: itemCustomization.displayType,
      minSelectable: itemCustomization.minSelectable,
      maxSelectable: itemCustomization.maxSelectable,
      isOptional: itemCustomization.isOptional,
      options:
        itemCustomization.options?.map((option) => {
          const optMin = (option as any).minQuantity ?? 1;
          const optMax = (option as any).maxQuantity ?? 1;
          return {
            customizationOptionId: option.customizationOptionId,
            itemId: option.customizationOptionId,
            name: getTitle(option.name),
            price: option.price / 100,
            isSelected: false,
            minQuantity: optMin,
            maxQuantity: optMax,
            quantity: optMin,
          };
        }) || [],
    }));
  };

  return {
    rawItem: item,
    title: getTitle(item.name),
    price: item.price / 100,
    description: getTitle(item.description) || "No description available.",
    customizations: mapCustomizations(),
  };
}

export interface UseProductPurchaseArgs {
  product: ProductData;
  attributes?: { attributeId: string; name: any; options?: any[] }[];
  variants?: ProductVariant[];
}

export function useProductPurchase({
  product,
  attributes = [],
  variants = [],
}: UseProductPurchaseArgs) {
  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const [customizations, setCustomizations] = useState<ProductCustomization[]>(
    product.customizations,
  );
  const [isWishlistActive, setIsWishlistActive] = useState(false);
  const [selectedOptionByAxis, setSelectedOptionByAxis] = useState<
    Record<string, string>
  >({});

  const isVariantItem = attributes.length > 0;
  const selectedVariant = isVariantItem
    ? findMatchingVariant(variants, selectedOptionByAxis)
    : undefined;
  const variantSelected = !isVariantItem || Boolean(selectedVariant);

  const navigate = useNavigate();

  const { addToCart } = useCart();
  const { addToWishlist, removeFromWishlist, wishlist } = useWishlistStore();

  const isWishlisted = wishlist.some((i) => i.itemId === product.rawItem.itemId);

  const toggleWishlist = () => {
    if (isWishlisted) {
      removeFromWishlist(product.rawItem.itemId);
    } else {
      addToWishlist(product.rawItem);
    }
    setIsWishlistActive(!isWishlistActive);
  };

  const handleCustomizationOptionSelect = (
    customizationId: string,
    optionItemId: string,
  ) => {
    setCustomizations((prevCustomizations) =>
      prevCustomizations.map((customization) => {
        if (customization.customizationId !== customizationId) {
          return customization;
        }

        const isRadioBehavior =
          customization.maxSelectable === 1 && !customization.isOptional;
        const maxReached =
          customization.options.filter((o) => o.isSelected).length >=
          customization.maxSelectable;

        const updatedOptions = customization.options.map((option) => {
          if (isRadioBehavior) {
            return {
              ...option,
              isSelected: option.itemId === optionItemId,
            };
          }

          if (option.itemId !== optionItemId) {
            return option;
          }

          const wouldExceedMax = !option.isSelected && maxReached;
          if (wouldExceedMax) {
            return option;
          }

          return { ...option, isSelected: !option.isSelected };
        });

        return { ...customization, options: updatedOptions };
      }),
    );
  };

  const handleOptionQuantityChange = (
    customizationId: string,
    optionItemId: string,
    delta: number,
  ) => {
    setCustomizations((prevCustomizations) =>
      prevCustomizations.map((customization) => {
        if (customization.customizationId !== customizationId) {
          return customization;
        }

        const updatedOptions = customization.options.map((option) => {
          if (option.itemId !== optionItemId) return option;

          const currentQty = option.quantity ?? option.minQuantity ?? 1;
          const optMin = option.minQuantity ?? 1;
          const optMax = option.maxQuantity ?? 1;
          const nextQty = Math.min(optMax, Math.max(optMin, currentQty + delta));

          return {
            ...option,
            quantity: nextQty,
            isSelected: nextQty > 0,
          };
        });

        return { ...customization, options: updatedOptions };
      }),
    );
  };

  const areCustomizationsValid = useCallback((): boolean => {
    return customizations.every((customization) => {
      const selectedCount = customization.options.filter(
        (option) => option.isSelected,
      ).length;

      if (customization.isOptional) {
        return (
          selectedCount === 0 ||
          (selectedCount >= customization.minSelectable &&
            selectedCount <= customization.maxSelectable)
        );
      }

      return (
        selectedCount >= customization.minSelectable &&
        selectedCount <= customization.maxSelectable
      );
    });
  }, [customizations]);

  const isCustomizationIncomplete =
    customizations.length > 0 && !areCustomizationsValid();
  const isVariantIncomplete = isVariantItem && !variantSelected;
  const canAddToCart = !isCustomizationIncomplete && !isVariantIncomplete;

  const formatCustomizationsForCart = (): any[] => {
    return customizations
      .filter((c) => c.options.some((o) => o.isSelected))
      .map((customization) => ({
        customizationId: customization.customizationId,
        name: customization.name,
        displayType: customization.displayType,
        minSelectable: customization.minSelectable,
        maxSelectable: customization.maxSelectable,
        isOptional: customization.isOptional,
        customizationOptions: customization.options
          .filter((option) => option.isSelected)
          .map((option) => ({
            customizationOptionId: option.customizationOptionId,
            itemId: option.itemId || option.customizationOptionId,
            name: option.name,
            price: option.price * 100,
            itemTax: product.rawItem.tax,
            quantity: option.quantity ?? option.minQuantity ?? 1,
          })),
      }));
  };

  const handleAddToCart = () => {
    if (customizations.length > 0 && !areCustomizationsValid()) {
      alert("Please select all required customization options.");
      return;
    }
    if (isVariantItem && !selectedVariant) {
      alert("Please select an option for each variant.");
      return;
    }

    setIsAdding(true);
    const formattedCustomizations = formatCustomizationsForCart();
    const variantPayload = selectedVariant
      ? {
          variantId: selectedVariant.variantId,
          sku: selectedVariant.sku,
          barcode: selectedVariant.barcode,
          name: getTitle(selectedVariant.name),
        }
      : undefined;

    const itemForCart = selectedVariant?.price != null
      ? { ...product.rawItem, itemPrice: selectedVariant.price }
      : product.rawItem;

    addToCart(itemForCart, formattedCustomizations, quantity, variantPayload);

    setTimeout(() => {
      setIsAdding(false);
    }, 600);
  };

  const handleBuyNow = () => {
    if (customizations.length > 0 && !areCustomizationsValid()) {
      alert("Please select all required customization options.");
      return;
    }
    if (isVariantItem && !selectedVariant) {
      alert("Please select an option for each variant.");
      return;
    }
    const formattedCustomizations = formatCustomizationsForCart();
    const variantPayload = selectedVariant
      ? {
          variantId: selectedVariant.variantId,
          sku: selectedVariant.sku,
          barcode: selectedVariant.barcode,
          name: getTitle(selectedVariant.name),
        }
      : undefined;

    const itemForCart = selectedVariant?.price != null
      ? { ...product.rawItem, itemPrice: selectedVariant.price }
      : product.rawItem;

    addToCart(itemForCart, formattedCustomizations, quantity, variantPayload);
    navigate({ to: "/c/checkout" });
  };

  const handleQuantityChange = (type: "increment" | "decrement") => {
    if (type === "increment") setQuantity((prev) => prev + 1);
    else if (type === "decrement" && quantity > 1)
      setQuantity((prev) => prev - 1);
  };

  const formatPrice = (price: string | number) => {
    if (typeof price === "number") return price.toFixed(2);
    return price;
  };

  const activePriceCents =
    selectedVariant && selectedVariant.price != null
      ? selectedVariant.price
      : product.rawItem.price;
  const activeCompareAtCents =
    (selectedVariant?.compareAtPrice as number | undefined) ??
    product.rawItem.compareAtPrice;
  const showCompareAt = !!(
    activeCompareAtCents && activeCompareAtCents > activePriceCents
  );
  const discountPercent = showCompareAt
    ? Math.round(
        ((activeCompareAtCents - activePriceCents) / activeCompareAtCents) *
          100,
      )
    : 0;

  return {
    quantity,
    isAdding,
    customizations,
    isWishlisted,
    selectedOptionByAxis,
    setSelectedOptionByAxis,
    isVariantItem,
    selectedVariant,
    variantSelected,
    isVariantIncomplete,
    isCustomizationIncomplete,
    canAddToCart,
    activePriceCents,
    activeCompareAtCents,
    showCompareAt,
    discountPercent,
    toggleWishlist,
    handleCustomizationOptionSelect,
    handleOptionQuantityChange,
    handleQuantityChange,
    handleAddToCart,
    handleBuyNow,
    formatPrice,
  };
}
